/**
 * nodeSelectionService - Scoring and capping for Mode A ("For You") and
 * Mode B ("Explore"). Direct implementation of sections 2-3 of
 * `related-node-algorithm.md`.
 *
 * Pipeline per call:
 *   1. Drop hidden node/rel types (belt-and-braces).
 *   2. Aggregate metadata nodes onto their parent.
 *   3. Compute score per candidate using priority × recency × typeBoost.
 *   4. Apply type cap (no one typename dominates a result set).
 *   5. Apply clustering thresholds from per-node-type profile.
 *   6. Diversity rescue: ensure at least 2 different types in output.
 *   7. 3P rescue: ensure at least 1 3P node if any were available.
 *
 * Pure functions, no React, no Jotai.
 */

import {
	ACTIVE_VERBS,
	findPriorityBand,
	getProfile,
	getTypeBoost,
	getTypeFamily,
	HIDDEN_NODE_TYPES,
	isAlwaysClusterRel,
	isHiddenRelType,
	isThirdParty,
	METADATA_NODE_TYPES,
	PRIORITY_WEIGHT,
	SIGNAL_WEIGHT,
} from './nodeProfileRegistry';

// ============================================================================
// Types
// ============================================================================

/** Input candidate (post-Cypher probe, pre-hydration). */
export type Candidate = {
	ari: string;
	typename: string;
	relTypes: string[];
	directions: Array<'in' | 'out'>;
	/** ISO timestamp or millisecond epoch — node's own `lastUpdated`. */
	lastActivity?: string | number | null;
	/** Optional 3P integration id — preserved when 3P discovery path. */
	integration?: string;
	/** Optional signal tags for Mode A. */
	signals?: string[];
	/**
	 * Raw `thirdPartyId` (preferred) or `thirdPartyAri` (fallback) value
	 * carried by the underlying graph node, when present. Used by the 3P
	 * rescue to detect connectors (e.g. bitbucket) whose External*
	 * typenames should NOT count as third-party for rescue purposes.
	 */
	thirdPartyHint?: string | null;
	/**
	 * Per-canonical-verb relationship `lastUpdated` (epoch ms). Populated
	 * by `flattenToCandidates` from `RelationshipInfo.lastUpdatedByNodeId`.
	 *
	 * Used by:
	 *   - `recencyDecay` input (prefer rel time over node time when present)
	 *   - same-day stacking dedup (group co-emitted same-day signals so the
	 *     stacker doesn't double-count `created+viewed+subscribed+snapshotted`
	 *     as four independent signals when they were one act)
	 */
	relLastUpdatedBySignal?: Map<string, number>;
	/** Convenience: max of `relLastUpdatedBySignal` values, cached on build. */
	maxRelLastUpdated?: number | null;
	/**
	 * Per-relType canonical verb derived by `flattenToCandidates` (which
	 * has access to the TWG relationship registry). Same length and same
	 * order as `relTypes`. Used by `pickPrimaryRel` to decide which rel
	 * is active without re-running the verb extractor (which needs the
	 * registry, not available in the scoring layer).
	 */
	verbsByRelType?: Map<string, string>;
};

/** Scored candidate ready for layout placement or clustering. */
export type ScoredNode = Candidate & {
	score: number;
	primaryRelType: string;
	primaryBandLevel: number;
	isBidirectional: boolean;
};

/** Partition after the full selection pipeline runs. */
export type SelectionResult = {
	/** Survived — hydrate and render as individual nodes. */
	kept: ScoredNode[];
	/** Collapsed into a '+N related' cluster. */
	clustered: ScoredNode[];
	/** Dropped entirely (e.g. hidden types). */
	dropped: ScoredNode[];
};

export type ModeAConfig = {
	totalSlots: number;
	/** Percentile for people slot budget. spec §2.6: `min(3, max(1, N // 5))`. */
	maxPeople?: number;
};

export type ModeBConfig = {
	sourceTypename: string;
	/** Defaults to `max(2, ceil(totalSlots * 0.4))`. */
	typeCap?: number;
	totalSlots: number;
};

// ============================================================================
// Scoring primitives
// ============================================================================

/** Exponential recency decay.
 *   - <24h: 1.0
 *   - 24h-7d: linear to 0.7
 *   - 7d-30d: linear to 0.3
 *   - >30d: 0.1 floor
 */
function recencyDecay(lastActivity: string | number | null | undefined): number {
	if (lastActivity == null) return 0.3;
	const t = typeof lastActivity === 'number' ? lastActivity : Date.parse(String(lastActivity));
	if (!Number.isFinite(t)) return 0.3;
	const ageMs = Date.now() - t;
	const ageHrs = ageMs / 3_600_000;
	if (ageHrs <= 24) return 1;
	if (ageHrs <= 24 * 7) return 1 - 0.3 * ((ageHrs - 24) / (24 * 6));
	if (ageHrs <= 24 * 30) return 0.7 - 0.4 * ((ageHrs - 24 * 7) / (24 * 23));
	return 0.1;
}

/**
 * Returns the recency timestamp to feed into `recencyDecay`. Prefers the
 * relationship-edge `lastUpdated` (set when the user last touched the
 * edge) over the node's own `lastUpdated` (which is the entity's last
 * modification regardless of who did it). Falls back to node activity
 * when no rel timestamp is available — preserving historical behaviour
 * for legacy / bridge rows that don't project relLastUpdated.
 *
 * HAR analysis (see relevancy-analysis.md) showed that ~45% of candidates
 * have no node activity at all but DO have a rel timestamp, and ~20%
 * have a rel timestamp newer than the node by a median 24 days — so
 * preferring rel time is the single largest "what was I working on"
 * improvement to relevancy.
 */
function preferredRecency(c: Candidate): string | number | null | undefined {
	return c.maxRelLastUpdated ?? c.lastActivity;
}

/**
 * Same-day-bucket dedup of stacked signals. Group signals by the day
 * (UTC) of their `relLastUpdatedBySignal` timestamp; within each day
 * keep only the highest-weight verb. Signals with no rel timestamp are
 * passed through as-is (we cannot prove they are co-emitted).
 *
 * This neutralises the ~40% score inflation on Confluence/Jira nodes
 * where one user act emits multiple signals on the same edge in the
 * same instant (e.g. `created` + `viewed` + `subscribed` + `snapshotted`
 * all stamped within milliseconds of one another). See HAR analysis
 * §"signal stacking" for the data behind the cutoff.
 */
function dedupSignalsForStacking(
	signals: string[],
	relTsBySignal: Map<string, number> | undefined,
): string[] {
	if (!relTsBySignal || relTsBySignal.size === 0) return signals;
	const noTs: string[] = [];
	const bestPerDay = new Map<number, { verb: string; weight: number }>();
	for (const s of signals) {
		const ts = relTsBySignal.get(s);
		if (ts == null) {
			noTs.push(s);
			continue;
		}
		const day = Math.floor(ts / 86_400_000);
		const w = SIGNAL_WEIGHT[s] ?? 0;
		const cur = bestPerDay.get(day);
		if (!cur || w > cur.weight) {
			bestPerDay.set(day, { verb: s, weight: w });
		}
	}
	return [...noTs, ...Array.from(bestPerDay.values()).map((b) => b.verb)];
}

/**
 * Pick the relationship to surface as the candidate's "primary" reason.
 *
 *   1. From `c.relTypes`, filter to those whose canonical verb is active
 *      (`ACTIVE_VERBS`) AND not always-clustered. Falls back to all rels
 *      if no actives are present.
 *   2. From the survivors pick the one whose priority band (per the
 *      source-node profile) has the lowest level (highest priority).
 *   3. Returns both the rel string and its band level so the caller can
 *      drop them straight into `toScored`.
 *
 * Why: previously the primary rel was simply `relTypes[0]` — i.e. discovery
 * order — which meant a node discovered through a passive `viewed-…`
 * edge first ended up labelled / clustered as passive even when an
 * active `owns-…` / `assigned-…` edge also surfaced it. Picking the
 * active edge restores the user-perceived relationship.
 */
function pickPrimaryRel(c: Candidate, sourceTypename: string): { rel: string; level: number } {
	const profile = getProfile(sourceTypename);
	const isActiveRel = (rel: string): boolean => {
		if (isAlwaysClusterRel(rel)) return false;
		// Prefer the canonical verb derived by `flattenToCandidates` (which
		// has the relMap). Falls back to checking the candidate's signals
		// list when no per-rel verb mapping is present (rare; legacy path).
		const verb = c.verbsByRelType?.get(rel);
		if (verb && ACTIVE_VERBS.has(verb)) return true;
		if (verb && (SIGNAL_WEIGHT[verb] ?? 0) >= 18) return true;
		return false;
	};
	const active = c.relTypes.filter(isActiveRel);
	const pool = active.length > 0 ? active : c.relTypes;
	let bestLevel = 7;
	let bestRel = pool[0];
	for (const r of pool) {
		const band = findPriorityBand(profile, r);
		const lvl = band?.level ?? 7;
		if (lvl < bestLevel) {
			bestLevel = lvl;
			bestRel = r;
		}
	}
	return { rel: bestRel, level: bestLevel };
}

// ============================================================================
// Mode A — For You
// ============================================================================

type ModeAInput = Candidate & {
	/** Mode-A-specific signal tags (e.g. `created`, `assigned`, `mentioned`). */
	signals: string[];
};

/**
 * Combine stacked signal weights. Highest signal takes full weight; each
 * additional signal contributes 20% of its own weight (spec §2.3).
 *
 * `relTsBySignal` (optional) lets the stacker collapse co-emitted same-day
 * signals into a single representative verb before stacking. Without it
 * the function reverts to the historical "stack everything" behaviour.
 */
function stackSignalWeights(signals: string[], relTsBySignal?: Map<string, number>): number {
	if (signals.length === 0) return 0;
	const effectiveSignals = dedupSignalsForStacking(signals, relTsBySignal);
	const weights = effectiveSignals.map((s) => SIGNAL_WEIGHT[s] ?? 0).sort((a, b) => b - a);
	let total = weights[0];
	for (let i = 1; i < weights.length; i++) {
		total += weights[i] * 0.2;
	}
	return total;
}

export function scoreForYou(inputs: ModeAInput[], config: ModeAConfig): SelectionResult {
	const dropped: ScoredNode[] = [];

	const scored: ScoredNode[] = [];
	for (const c of inputs) {
		if (HIDDEN_NODE_TYPES.has(c.typename) || METADATA_NODE_TYPES.has(c.typename)) {
			dropped.push(toScored(c, 0, c.relTypes[0] ?? '', 7));
			continue;
		}
		const signalWeight = stackSignalWeights(c.signals, c.relLastUpdatedBySignal);
		const base = signalWeight * recencyDecay(preferredRecency(c)) * getTypeBoost(c.typename);
		// Breadth bonus uses the dedup'd signal count too — same-day
		// co-emitted signals shouldn't inflate the bonus either.
		const dedupedCount = dedupSignalsForStacking(c.signals, c.relLastUpdatedBySignal).length;
		const breadthBonus = Math.min(dedupedCount - 1, 3) * 2;
		const { rel, level } = pickPrimaryRel(c, 'AtlassianUser');
		scored.push(toScored(c, base + breadthBonus, rel, level));
	}

	scored.sort((a, b) => b.score - a.score);

	// People slot budget
	const peopleSlots = Math.min(3, Math.max(1, Math.floor(config.totalSlots / 5)));
	const peopleCap = config.maxPeople ?? peopleSlots;

	const people: ScoredNode[] = [];
	const others: ScoredNode[] = [];
	for (const s of scored) {
		if (s.typename === 'AtlassianUser') people.push(s);
		else others.push(s);
	}

	const peopleKept = people.slice(0, peopleCap);
	const remainingSlots = config.totalSlots - peopleKept.length;

	// Type cap — no single non-people typename can occupy more than
	// max(2, ceil(visible_slots × 0.4)) of the visible slots. This
	// prevents e.g. a wall of Confluence pages from dominating the
	// "for you" view.
	const typeCap = computeTypeCap(config.totalSlots);
	const { kept: othersAfterCap, overflow: othersTypeCapOverflow } = applyTypeCap(others, typeCap);

	let otherKept = othersAfterCap.slice(0, remainingSlots);
	const otherSlotOverflow = othersAfterCap.slice(remainingSlots);

	otherKept = applyDiversityRescue(otherKept, othersAfterCap, 2);
	otherKept = applyThreePRescue(otherKept, othersAfterCap, 1);

	return {
		kept: [...peopleKept, ...otherKept],
		clustered: [...otherSlotOverflow, ...othersTypeCapOverflow],
		dropped,
	};
}

// ============================================================================
// Mode B — Explore
// ============================================================================

export function scoreExplore(inputs: Candidate[], config: ModeBConfig): SelectionResult {
	const dropped: ScoredNode[] = [];

	const scored: ScoredNode[] = [];
	for (const c of inputs) {
		if (HIDDEN_NODE_TYPES.has(c.typename)) {
			dropped.push(toScored(c, 0, c.relTypes[0] ?? '', 7));
			continue;
		}
		if (METADATA_NODE_TYPES.has(c.typename)) {
			// metadata gets folded onto parent upstream, skip here
			continue;
		}
		const filteredRels = c.relTypes.filter((r) => !isHiddenRelType(r));
		if (filteredRels.length === 0) {
			dropped.push(toScored(c, 0, c.relTypes[0] ?? '', 7));
			continue;
		}
		// Pick the active-first primary rel (active wins over passive even
		// when the passive edge has a higher priority band; falls back to
		// the historical lowest-band-wins behaviour when no actives are
		// present). Restricted to the rels that survived the hide-list
		// filter above so we never surface a hidden rel as the primary.
		const candidateForPicking: Candidate = { ...c, relTypes: filteredRels };
		const { rel: bestRel, level: bestLevel } = pickPrimaryRel(
			candidateForPicking,
			config.sourceTypename,
		);
		const priorityW = PRIORITY_WEIGHT[bestLevel as 1 | 2 | 3 | 4 | 5 | 6 | 7];
		const score = priorityW * recencyDecay(preferredRecency(c)) * getTypeBoost(c.typename);
		scored.push(toScored(c, score, bestRel, bestLevel));
	}

	scored.sort((a, b) => b.score - a.score);

	// Type cap — no single typename can occupy more than
	// max(2, ceil(visible_slots × 0.4)) of the visible slots.
	const typeCap = config.typeCap ?? computeTypeCap(config.totalSlots);
	const { kept: afterCap, overflow: typeCapOverflow } = applyTypeCap(scored, typeCap);

	// Clustering thresholds — group members whose band says to cluster
	const { individual, forced } = partitionByClustering(afterCap, config.sourceTypename);

	let kept = individual.slice(0, config.totalSlots);
	const slotOverflow = individual.slice(config.totalSlots);

	kept = applyDiversityRescue(kept, individual, 2);
	kept = applyThreePRescue(kept, individual, 1);

	return {
		kept,
		// Items that lost their slot to either the type cap or the visible
		// slot budget become candidates for the clustered/grouped bucket.
		clustered: [...forced, ...slotOverflow, ...typeCapOverflow],
		dropped,
	};
}

// ============================================================================
// Shared helpers
// ============================================================================

function toScored(
	c: Candidate,
	score: number,
	primaryRelType: string,
	primaryBandLevel: number,
): ScoredNode {
	return {
		...c,
		score,
		primaryRelType,
		primaryBandLevel,
		isBidirectional: c.directions.includes('in') && c.directions.includes('out'),
	};
}

/**
 * Split a scored list into a per-type-family–capped `kept` set plus the
 * `overflow` items that exceeded the cap. Surplus items are returned
 * separately so callers can either drop them, defer them to the
 * clustering bucket, or otherwise exclude them from the visible slot
 * budget — they MUST NOT silently re-enter the kept selection.
 *
 * Counting is by **type family** (see `getTypeFamily` in
 * `nodeProfileRegistry`), not raw typename. This is important because
 * the discovery layer surfaces the same logical content under multiple
 * Atlassian-side typenames — e.g. a Loom recording arrives as both
 * `LoomVideo` and `ExternalVideo`. Counting them independently makes
 * the cap leak: each typename respects its 5-slot cap individually,
 * but their combined visual share dominates the result set. Grouping
 * them into a single family bucket ensures the user-perceived
 * proportion respects the configured cap.
 *
 * No single type family in `kept` will exceed `cap` items.
 */
function applyTypeCap(
	scored: ScoredNode[],
	cap: number,
): { kept: ScoredNode[]; overflow: ScoredNode[] } {
	const counts = new Map<string, number>();
	const kept: ScoredNode[] = [];
	const overflow: ScoredNode[] = [];
	for (const s of scored) {
		const family = getTypeFamily(s.typename);
		const c = counts.get(family) ?? 0;
		if (c < cap) {
			kept.push(s);
			counts.set(family, c + 1);
		} else {
			overflow.push(s);
		}
	}
	return { kept, overflow };
}

/**
 * Compute the per-type cap from the visible slot budget.
 *
 * `type_cap = max(2, ceil(visible_slots × 0.4))`
 *
 * No single entity type can occupy more than this many slots in the
 * selected set.
 */
function computeTypeCap(visibleSlots: number): number {
	return Math.max(2, Math.ceil(visibleSlots * 0.4));
}

/**
 * Separate nodes that must be clustered (per profile threshold or the
 * always-cluster rel list) from those that can stand alone.
 */
function partitionByClustering(
	nodes: ScoredNode[],
	sourceTypename: string,
): { individual: ScoredNode[]; forced: ScoredNode[] } {
	const profile = getProfile(sourceTypename);
	const countByRel = new Map<string, number>();
	for (const n of nodes) {
		countByRel.set(n.primaryRelType, (countByRel.get(n.primaryRelType) ?? 0) + 1);
	}
	const individual: ScoredNode[] = [];
	const forced: ScoredNode[] = [];
	for (const n of nodes) {
		if (isAlwaysClusterRel(n.primaryRelType)) {
			forced.push(n);
			continue;
		}
		const band = findPriorityBand(profile, n.primaryRelType);
		const threshold = band?.clusterThreshold ?? 10;
		if (threshold === 'never') {
			individual.push(n);
			continue;
		}
		if (threshold === 'always') {
			forced.push(n);
			continue;
		}
		const cnt = countByRel.get(n.primaryRelType) ?? 0;
		if (cnt > threshold) {
			forced.push(n);
		} else {
			individual.push(n);
		}
	}
	return { individual, forced };
}

/**
 * Ensure `kept` contains at least `minTypes` distinct typenames by
 * promoting candidates from `pool`. Promoted candidates replace the
 * lowest-scoring same-type members.
 */
function applyDiversityRescue(
	kept: ScoredNode[],
	pool: ScoredNode[],
	minTypes: number,
): ScoredNode[] {
	const present = new Set(kept.map((n) => n.typename));
	if (present.size >= minTypes) return kept;
	const promotions: ScoredNode[] = [];
	for (const p of pool) {
		if (!present.has(p.typename) && !kept.includes(p)) {
			promotions.push(p);
			present.add(p.typename);
			if (present.size >= minTypes) break;
		}
	}
	if (promotions.length === 0) return kept;
	// Drop lowest-scoring already-seen duplicates to make room.
	const typeCounts = new Map<string, number>();
	for (const k of kept) typeCounts.set(k.typename, (typeCounts.get(k.typename) ?? 0) + 1);
	const sorted = [...kept].sort((a, b) => a.score - b.score);
	const droppedIds = new Set<string>();
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	for (const _promo of promotions) {
		const victim = sorted.find(
			(s) => (typeCounts.get(s.typename) ?? 0) > 1 && !droppedIds.has(s.ari),
		);
		if (victim) {
			droppedIds.add(victim.ari);
			typeCounts.set(victim.typename, (typeCounts.get(victim.typename) ?? 0) - 1);
		}
	}
	return [...kept.filter((k) => !droppedIds.has(k.ari)), ...promotions];
}

/**
 * Detects whether a `thirdPartyId` / `thirdPartyAri` hint identifies the
 * Bitbucket connector. Bitbucket data flows through the 3P pipeline as
 * `External*` typenames but is owned by Atlassian, so it must NOT count
 * toward the 3P rescue quota.
 *
 * Recognised shapes (mirrors `productRegistry.getProductForThirdPartyId`):
 *   - ARI form:           `ari:cloud:bitbucket::pullrequest/abc:123`
 *   - Dotted ARI segment: `bitbucket.bitbucket`
 *   - Connector form:     `bitbucket::pullrequest/123`
 *   - Bare connector:     `bitbucket`
 */
function isBitbucketThirdPartyHint(hint: string | null | undefined): boolean {
	if (!hint) return false;
	const lower = hint.toLowerCase();
	if (lower.startsWith('ari:')) {
		// ARI segment containing the resource owner. Both
		// `ari:cloud:bitbucket::*` and `ari:cloud:graph::*/bitbucket.bitbucket/*`
		// shapes embed the literal `bitbucket` token in a colon-delimited
		// segment.
		const segments = lower.split(':');
		if (segments.some((seg) => seg === 'bitbucket' || seg.startsWith('bitbucket.'))) {
			return true;
		}
	}
	// Connector token is the leading segment before `::`, `:` or `/`.
	const connectorToken = lower.split(/::|:|\//)[0] ?? lower;
	return connectorToken === 'bitbucket' || connectorToken.startsWith('bitbucket.');
}

/**
 * Returns true when a scored node should count toward the 3P rescue
 * quota: `External*` typename AND not a bitbucket-bridged node. Bitbucket
 * surfaces as `External*` over the 3P pipeline but is first-party so it
 * is excluded here (and only here — other `isThirdParty` call sites are
 * intentionally untouched).
 */
function isRescueEligibleThirdParty(node: ScoredNode): boolean {
	if (!isThirdParty(node.typename)) return false;
	if (isBitbucketThirdPartyHint(node.thirdPartyHint)) return false;
	return true;
}

/**
 * Ensure at least `minCount` 3P (External*) nodes survive if any exist
 * in `pool`. Mirrors diversity rescue but targeted at 3P.
 *
 * Bitbucket-bridged External nodes are explicitly NOT considered 3P for
 * the purposes of this rescue — they are first-party Atlassian content
 * that happens to flow through the External* typename pipeline.
 */
function applyThreePRescue(kept: ScoredNode[], pool: ScoredNode[], minCount: number): ScoredNode[] {
	const presentCount = kept.filter(isRescueEligibleThirdParty).length;
	if (presentCount >= minCount) return kept;
	const available3P = pool.filter((p) => isRescueEligibleThirdParty(p) && !kept.includes(p));
	if (available3P.length === 0) return kept;
	const needed = minCount - presentCount;
	const promos = available3P.slice(0, needed);
	// Drop lowest-scoring non-3P to make room (or fall through if none).
	// "non-3P" here uses the same rescue-eligibility lens so a kept
	// bitbucket-bridged External is itself a valid eviction victim.
	const non3P = [...kept]
		.filter((k) => !isRescueEligibleThirdParty(k))
		.sort((a, b) => a.score - b.score);
	const droppedIds = new Set<string>();
	for (let i = 0; i < promos.length && i < non3P.length; i++) {
		droppedIds.add(non3P[i].ari);
	}
	return [...kept.filter((k) => !droppedIds.has(k.ari)), ...promos];
}

// ============================================================================
// Convenience — convert SelectionResult into ChildSpecs for layout
// ============================================================================
